import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import {
  fetchProfile,
  fetchHomeMembers,
  type Profile,
} from "../lib/auth";
import {
  createHousehold,
  fetchMyHouseholds,
  type Household,
} from "../lib/households";
import { countPlantsInHousehold, fetchPlants, type Plant } from "../lib/plants";
import PlantCard from "../components/PlantCard";
import HouseholdSelector, {
  type HouseholdSummary,
} from "../components/HouseholdSelector";

export default function HomeScreen() {
  const navigate = useNavigate();
  const [plants, setPlants] = useState<Plant[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [households, setHouseholds] = useState<HouseholdSummary[]>([]);
  const [activeHouseholdId, setActiveHouseholdId] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [plantsData, { data: auth }] = await Promise.all([
        fetchPlants(),
        supabase.auth.getUser(),
      ]);
      setPlants(plantsData);

      if (auth.user) {
        const [p, homes] = await Promise.all([
          fetchProfile(auth.user.id),
          fetchMyHouseholds(),
        ]);
        setProfile(p);

        const summaries: HouseholdSummary[] = await Promise.all(
          homes.map(async (h: Household) => {
            const [plantCount, members] = await Promise.all([
              countPlantsInHousehold(h.id),
              fetchHomeMembers(h.id),
            ]);
            return { id: h.id, name: h.name, plantCount, members };
          })
        );
        setHouseholds(summaries);
        setActiveHouseholdId((prev) => {
          if (prev && summaries.some((s) => s.id === prev)) return prev;
          return summaries[0]?.id || null;
        });
      }
    } catch (err) {
      console.error("home load failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreateHousehold(name: string) {
    await createHousehold(name);
    await load();
  }

  const firstName = (profile?.display_name || "there").split(" ")[0];
  const visiblePlants = activeHouseholdId
    ? plants.filter((p) => p.household_id === activeHouseholdId)
    : [];
  const now = Date.now();
  const thirstyCount = visiblePlants.filter((p) => {
    const next = new Date(p.next_watering).getTime();
    return next - now <= 24 * 60 * 60 * 1000;
  }).length;

  const initial = (profile?.display_name || "?").charAt(0).toUpperCase();

  if (loading) {
    return <div className="loading-screen">Loading your plants…</div>;
  }

  return (
    <div className="app-shell home">
      <div className="home__top-row">
        <div className="home__greeting-wrap">
          <h1 className="home__greeting">Hello, {firstName}</h1>
        </div>
        <button
          type="button"
          className="home__avatar-btn"
          onClick={() => navigate("/settings")}
          aria-label="Settings"
        >
          {profile?.avatar_url ? (
            <img src={profile.avatar_url} alt="" />
          ) : (
            <span>{initial}</span>
          )}
        </button>
      </div>

      {thirstyCount > 0 && (
        <div className="home__alert">
          <div className="home__alert-icon">💧</div>
          <div className="home__alert-body">
            <div className="home__alert-title">Watering alert</div>
            <div className="home__alert-sub">
              {thirstyCount}{" "}
              {thirstyCount === 1 ? "plant needs" : "plants need"} your
              attention today.
            </div>
          </div>
          <div className="home__alert-chev">›</div>
        </div>
      )}

      {households.length > 0 && (
        <HouseholdSelector
          households={households}
          activeId={activeHouseholdId}
          onChangeActive={setActiveHouseholdId}
          onManage={() => navigate("/housemates")}
          onCreate={handleCreateHousehold}
        />
      )}

      {visiblePlants.length === 0 ? (
        <div className="home__empty">
          <div className="home__empty-icon">🪴</div>
          <div className="home__empty-title">No plants yet</div>
          <div className="home__empty-sub">
            Tap "Add new plant" to get started
          </div>
        </div>
      ) : (
        <div className="home__grid">
          {visiblePlants.map((p) => (
            <PlantCard
              key={p.id}
              plant={p}
              onClick={() => navigate(`/plant/${p.id}`)}
            />
          ))}
        </div>
      )}

      <div className="sticky-cta-wrap">
        <button
          type="button"
          className="primary-btn"
          onClick={() => navigate("/add-plant")}
        >
          Add new plant
        </button>
      </div>

      <style>{`
        .home__top-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding-top: 32px;
          padding-bottom: 8px;
        }
        .home__greeting-wrap { flex: 1; min-width: 0; }
        .home__greeting {
          font-family: Georgia, serif;
          font-weight: 700;
          font-size: 26px;
          color: #1f5a39;
          margin: 0;
        }
        .home__avatar-btn {
          width: 44px;
          height: 44px;
          border-radius: 22px;
          border: 1.5px solid #2D7A4F;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
        }
        .home__avatar-btn img { width: 100%; height: 100%; object-fit: cover; }
        .home__avatar-btn span { font-size: 16px; font-weight: 700; color: #2D7A4F; }
        .home__alert {
          background: #fff;
          border-radius: 16px;
          padding: 14px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.06);
        }
        .home__alert-icon {
          width: 40px; height: 40px; border-radius: 20px;
          background: #2D7A4F;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px;
        }
        .home__alert-body { flex: 1; }
        .home__alert-title {
          font-size: 14px; font-weight: 700; color: #1f2937;
        }
        .home__alert-sub {
          font-size: 12px; color: #6b7a6e; margin-top: 2px;
        }
        .home__alert-chev {
          font-size: 22px; color: #6b7a6e;
        }
        .home__grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
          margin-top: 4px;
          position: relative;
          z-index: 0;
        }
        .home__empty {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          text-align: center;
          padding: 60px 0;
        }
        .home__empty-icon { font-size: 64px; }
        .home__empty-title {
          font-size: 20px;
          font-weight: 700;
          color: #1f5a39;
        }
        .home__empty-sub {
          font-size: 14px;
          color: #6b7a6e;
        }
      `}</style>
    </div>
  );
}
